import { useState, type FormEvent, useEffect, useRef } from "react";

import {
  useTagsQuery,
  useAddTagMutation,
  useDeleteLabelMutation,
} from "./api/taskApi";
import ColumnText from "@/shared/components/molecules/ColumnText";
import ColumnActionsDropdown from "@/shared/components/molecules/ColumnActionsDropdown";
import Table from "@/shared/components/organisms/Table";
import { PlusIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { useUpdateMarketingTagMutation } from "@/app/marketing-tags/marketing-tags.api";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import { skipToken } from "@reduxjs/toolkit/query/react";

interface Tag {
  id: string;
  label: string;
  branch_id: number;
}

interface TagManagerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

interface Branch {
  id: number;
  // Add other branch properties if needed
}

const TagManager: React.FC<TagManagerProps> = ({ open, setOpen, branch }) => {
  const {
    data: tags,
    isLoading,
    refetch,
  } = useTagsQuery(branch ? { branch_id: branch } : skipToken);

  const [addTag, { isLoading: isAddingTag }] = useAddTagMutation();
  const [updateTag, { isLoading: isUpdatingTag }] =
    useUpdateMarketingTagMutation();
  const [deleteLabel, { isLoading: isDeletingLabel }] =
    useDeleteLabelMutation();

  const newInputRef = useRef<HTMLInputElement>(null);

  const [isAddingOrEditing, setIsAddingOrEditing] = useState(false);
  const [tagValue, setTagValue] = useState("");
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [editingTagLabel, setEditingTagLabel] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ name?: string }>({});

  useEffect(() => {
    if (isAddingOrEditing && newInputRef.current) {
      newInputRef.current.focus();
    }
  }, [isAddingOrEditing]);

  const validateInput = (value: string) => {
    if (!value.trim()) {
      return "El nombre de la etiqueta es requerido";
    }
    return "";
  };

  const handleAddNewTag = () => {
    if (!branch) return; // Prevent action if branch is undefined
    setIsAddingOrEditing(true);
    setTagValue("");
    setEditingTagId(null);
    setEditingTagLabel(null);
    setErrors({});
  };

  const handleEditTag = (tag: Tag) => {
    setIsAddingOrEditing(true);
    setTagValue(tag.label);
    setEditingTagId(tag.id);
    setEditingTagLabel(tag.label);
    setErrors({});
  };

  const handleCancelAddOrEdit = () => {
    setIsAddingOrEditing(false);
    setTagValue("");
    setEditingTagId(null);
    setEditingTagLabel(null);
    setErrors({});
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setTagValue(value);
    const error = validateInput(value);
    setErrors({ name: error });
  };

  const handleSaveTag = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const error = validateInput(tagValue);
    if (error) {
      setErrors({ name: error });
      return;
    }

    if (!branch) return; // Prevent action if branch is undefined

    try {
      if (editingTagId) {
        console.log("editingTagId", {
          id: editingTagId,
          patch: {
            label: tagValue,
          },
        });
        await updateTag({
          id: editingTagId,
          patch: {
            label: tagValue,
          },
        }).unwrap();
      } else {
        await addTag({
          branch_id: branch,
          label: tagValue,
        }).unwrap();
      }
      setTagValue("");
      setEditingTagId(null);
      setEditingTagLabel(null);
      setIsAddingOrEditing(false);
      refetch();
    } catch (err) {
      console.error("Error saving tag:", err);
      setErrors({ name: "Error al guardar la etiqueta" });
    }
  };

  const handleDeleteTag = async (tagId: string) => {
    try {
      await deleteLabel(tagId).unwrap();
      refetch();
    } catch (err) {
      console.error("Error deleting tag:", err);
    }
  };

  const columns = [
    {
      accessor: (tag: Tag) => tag.label,
      Header: "Etiqueta",
      header: "Etiqueta",
      render: (tag: Tag) => (
        <ColumnText title={tag.label} className="w-full text-left" />
      ),
    },
    {
      accessor: (tag: Tag) => tag.id,
      Header: "",
      header: "",
      render: (tag: Tag) => (
        <div className="flex justify-end">
          <ColumnActionsDropdown
            name={tag.label}
            options={[
              {
                name: "Eliminar",
                functionMenu: () => handleDeleteTag(tag.id),
              },
            ]}
            isLast={true}
            onClick={() => handleEditTag(tag)}
            moreButton={true}
          />
        </div>
      ),
    },
  ];

  const fadeVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  };

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-0">
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800"></div>
        </div>
      </div>
    );
  }

  return (
    <DialogCreate
      open={open}
      tittleButtonCreate={isAddingOrEditing ? "crear" : undefined}
      tittleButtonCancel="Cancelar"
      isLoading={isAddingTag || isUpdatingTag || isDeletingLabel}
      OnclickCancel={() => {
        handleCancelAddOrEdit();
        setOpen(false);
      }}
      setOpen={setOpen}
      OnclickCreate={handleSaveTag as any}
      onBack={
        !isAddingOrEditing
          ? undefined
          : () => {
              handleCancelAddOrEdit();
            }
      }
    >
      <div className=" sm:px-6 lg:px-0">
        <div
          className="mt-2 flow-root relative"
          style={{
            alignSelf: "right",
            marginTop: "-1rem",
            marginRight: "-1rem",
            marginLeft: "-1rem",
          }}
        >
          <AnimatePresence mode="wait">
            {!isAddingOrEditing ? (
              <motion.div
                key="table-view"
                initial="initial"
                animate="animate"
                exit="exit"
                variants={fadeVariants}
                transition={{ duration: 0.2, ease: "easeInOut" }}
              >
                <Table
                  data={tags || []}
                  columns={columns}
                  renderHeaderExtras={() => (
                    <button
                      onClick={handleAddNewTag}
                      type="button"
                      className="rounded-full border-2 border-dashed border-gray-400 p-1 text-gray-400 shadow-sm hover:text-gray-500 focus-visible:outline-none"
                    >
                      <PlusIcon aria-hidden="true" className="h-5 w-5" />
                    </button>
                  )}
                />
              </motion.div>
            ) : (
              <motion.div
                key="input-view"
                initial="initial"
                animate="animate"
                exit="exit"
                variants={fadeVariants}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="relative"
              >
                <div className="mb-4 p-10">
                  <form onSubmit={handleSaveTag}>
                    <div className="mt-6 mx-2">
                      <div className="space-y-12">
                        <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                          <div className="flex items-center justify-center h-full text-center">
                            <div>
                              <h2 className="text-base font-semibold leading-7 text-gray-900">
                                Características de la Etiqueta
                              </h2>
                            </div>
                          </div>
                          <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                            <div className="col-span-full">
                              <InputWithCornerHint
                                label="Nombre de la Etiqueta"
                                type="text"
                                onChange={handleChange}
                                name="name"
                                placeholder="Nombre de la Etiqueta"
                                error={!!errors.name}
                                textError={errors.name}
                                value={tagValue}
                                ref={newInputRef}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </DialogCreate>
  );
};

export default TagManager;
